/* eslint react-hooks/exhaustive-deps: "warn" */

import { useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './RegisProvenance.module.scss';

type Translate = (key: string) => string;

type Note = {
  n: number;
  field: string;
  desc: string;
};

const getNotes = (t: Translate): Note[] => [
  {
    n: 1,
    field: 'source.register',
    desc: t(
      "Names the government body that publishes the record, so the answer resolves to the statutory source rather than to a vendor's own dataset.",
    ),
  },
  {
    n: 2,
    field: 'source.filing_reference',
    desc: t(
      'The document identifier as the register issued it. A reviewer can pull the original filing from the register without going through us.',
    ),
  },
  {
    n: 3,
    field: 'source.retrieved_at',
    desc: t(
      'Fixes what the register held at the moment the decision was made, which is the question asked months later when the record has moved on.',
    ),
  },
  {
    n: 4,
    field: 'resolved_entity.registration_number',
    desc: t(
      'Shows which of several similarly named companies the answer is actually about. Most disputes about company data are resolution errors, not value errors.',
    ),
  },
  {
    n: 5,
    field: 'unavailable_fields',
    desc: t(
      'Lists what the register does not publish for that jurisdiction, so a missing value is distinguishable from a value of zero or none.',
    ),
  },
];

const marker = (i: number, active: number | null) =>
  `<span class="mk${active === i ? ' mkActive' : ''}" data-i="${i}">${i}</span>`;

const buildCodeHtml = (active: number | null) => `{
  <span class="k">"field"</span>: <span class="s">"status"</span>,
  <span class="k">"value"</span>: <span class="s">"Active"</span>,
  <span class="k">"source"</span>: {
    <span class="k">"register"</span>: <span class="s">"Companies House"</span>,${marker(1, active)}
    <span class="k">"jurisdiction"</span>: <span class="s">"GB"</span>,
    <span class="k">"filing_reference"</span>: <span class="s">"CS01"</span>,${marker(2, active)}
    <span class="k">"filed_on"</span>: <span class="s">"2026-01-28"</span>,
    <span class="k">"retrieved_at"</span>: <span class="s">"2026-08-04T05:43:17Z"</span>${marker(3, active)}
  },
  <span class="k">"resolved_entity"</span>: {
    <span class="k">"name"</span>: <span class="s">"Global Data Intelligence Limited"</span>,
    <span class="k">"registration_number"</span>: <span class="s">"09410808"</span>${marker(4, active)}
  },
  <span class="k">"unavailable_fields"</span>: []${marker(5, active)}
}`;

export const RegisProvenance = () => {
  const { t } = useInt();
  const rootRef = useRef<HTMLDivElement>(null);
  const visible = useIntersection(rootRef, { threshold: 0.1 });
  const [active, setActive] = useState<number | null>(null);

  const notes = getNotes(t);

  return (
    <Section className={styles.root}>
      <div ref={rootRef} className={cn(styles.content, visible && styles.contentIn)}>
        <div className={styles.head}>
          <TitleBlock
            align="left"
            title={t('What arrives with the answer.')}
            description={t(
              'The written answer is the readable part. The part that survives an audit is what sits underneath it.',
            )}
          />
        </div>

        <div className={styles.prov}>
          <div className={styles.provCode}>
            <div className={styles.provCodeHead}>
              <span>{t('response fragment')}</span>
              <span className={styles.provCodeStatus}>{t('one field')}</span>
            </div>
            <pre className={styles.pre} dangerouslySetInnerHTML={{ __html: buildCodeHtml(active) }} />
          </div>

          <ol className={styles.notes}>
            {notes.map((note) => (
              <li
                key={note.n}
                className={cn(styles.note, active === note.n && styles.noteActive)}
                onMouseEnter={() => setActive(note.n)}
                onMouseLeave={() => setActive(null)}
              >
                <span className={styles.noteN}>{note.n}</span>
                <div>
                  <b>{note.field}</b>
                  <p>{note.desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className={styles.row}>
          <div className={styles.rowKey}>{t('On the structured endpoints')}</div>
          <div className={styles.rowValue}>
            {t('Send')} <code>include_provenance: true</code>{' '}
            {t(
              'on enrichment, autocomplete and financials calls to get source metadata alongside the values. Documented in the v2 reference.',
            )}
          </div>
        </div>
      </div>
    </Section>
  );
};
